import React, { useState } from "react";
import Input from "../Input";
import cx from "classnames";
import styles from "./styles.module.scss";
import ErrorSwitch from "../../ErrorSwitch";
import useValidations from "../../../utils/useValidations";
import { has } from "lodash";
import t from "../../../utils/translate";
import { FIRST_NAME, LAST_NAME } from "../constants";
import useAppContext from "../../AppContext";
import useForceValidate from "../../../utils/useForceValidate";

const FIELD_META = {
  [FIRST_NAME]: {
    name: FIRST_NAME,
    id: FIRST_NAME,
    label: t("FIRST_NAME_LABEL"),
    minLengthError: t("FIRST_NAME_REQUIRED_ERROR"),
    maxLengthError: t("MAX_20_CHARS_ERROR"),
    requiredError: t("FIRST_NAME_REQUIRED_ERROR"),
    invalidPatternError: t("INVALID_NAME_PATTERN_ERROR")
  },
  [LAST_NAME]: {
    name: LAST_NAME,
    id: LAST_NAME,
    label: t("LAST_NAME_LABEL"),
    minLengthError: t("LAST_NAME_REQUIRED_ERROR"),
    maxLengthError: t("MAX_20_CHARS_ERROR"),
    requiredError: t("LAST_NAME_REQUIRED_ERROR"),
    invalidPatternError: t("INVALID_NAME_PATTERN_ERROR")
  }
};

const isValidRegex = (value, regex, regexFlags) => {
  const pattern = new RegExp(regex, regexFlags);
  return pattern.test(value);
};

function BuildInput({
  config,
  defaultValue = "",
  onChange: broadcastChange,
  forceValidation,
  newQuiz
}) {
  const [value, setValue] = useState(() => defaultValue);
  const [isValid, validations, validate] = useValidations({
    isLessThanMin: value =>
      has(config, "min") && config.min > String(value).length,
    isMoreThanMax: value =>
      has(config, "max") && config.max < String(value).length,
    isInvalidPattern: value =>
      has(config, "regex") &&
      !isValidRegex(value, config.regex, config.regexFlags),
    isRequired: value => config.required === true && String(value).length === 0
  });

  const { hasTriggered } = useForceValidate({
    trigger: forceValidation,
    validate: () => validate(value || "")
  });

  const onChange = ev => {
    let { name: key, value } = ev.target;
    setValue(value);
    value = validate(value)[0] ? value : null;
    broadcastChange(key, value);
  };

  const renderError =
    !newQuiz.isSequence || (newQuiz.isSequence && hasTriggered);

  return (
    <div
      className={cx(styles.inputWrap, {
        [styles.hasValue]: value && isValid && newQuiz.isClinical
      })}
    >
      {config.noPlaceholder && (
        <label className={styles.header} htmlFor={config.name}>
          {config.label}
        </label>
      )}
      {!config.noPlaceholder && (
        <label className={styles.hidden} htmlFor={config.name}>
          {t("FIRST_NAME_LABEL")}
        </label>
      )}
      <Input
        data-testid={config.name}
        id={config.id}
        value={value ?? ""}
        name={config.name}
        onChange={onChange}
        error={!isValid}
        placeholder={config.noPlaceholder ? "" : t("FIRST_NAME_LABEL")}
      />
      {renderError && (
        <ErrorSwitch name="name-form" validations={validations}>
          {{
            isLessThanMin: config.minLengthError,
            isMoreThanMax: config.maxLengthError,
            isRequired: config.requiredError,
            isInvalidPattern: config.invalidPatternError
          }}
        </ErrorSwitch>
      )}
    </div>
  );
}

export default function NameForm({
  onChange,
  value,
  rules,
  noPlaceholder = false
}) {
  const { newQuiz, forceValidation } = useAppContext();
  const isSequence = newQuiz?.isSequence;
  const { firstName: firstNameValidations, lastName: lastNameValidations } =
    rules;

  const sequenceConfig = {
    [FIRST_NAME]: {
      ...FIELD_META[FIRST_NAME],
      minLengthError: t("FIELD_ERROR_REQUIRED"),
      requiredError: t("FIELD_ERROR_REQUIRED"),
      label: "",
      required: true
    },
    [LAST_NAME]: {
      ...FIELD_META[LAST_NAME],
      minLengthError: t("FIELD_ERROR_REQUIRED"),
      requiredError: t("FIELD_ERROR_REQUIRED"),
      label: "",
      required: true
    }
  };

  return (
    <div className={styles.container}>
      <div className={cx(styles.row, styles.name)}>
        <BuildInput
          defaultValue={value[FIRST_NAME]}
          config={{
            ...firstNameValidations,
            ...FIELD_META[FIRST_NAME],
            ...(isSequence ? sequenceConfig[FIRST_NAME] : {}),
            noPlaceholder: isSequence ? false : noPlaceholder
          }}
          onChange={onChange}
          forceValidation={forceValidation}
          newQuiz={newQuiz}
        />
      </div>
      {lastNameValidations && (
        <div className={cx(styles.row, styles.name)}>
          <BuildInput
            defaultValue={value[LAST_NAME]}
            config={{
              ...lastNameValidations,
              ...FIELD_META[LAST_NAME],
              ...(isSequence ? sequenceConfig[LAST_NAME] : {})
            }}
            onChange={onChange}
            newQuiz={newQuiz}
          />
        </div>
      )}
    </div>
  );
}
